import React, { useCallback, useMemo, useState } from 'react';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import StandardsBrowserPanel from '../StandardsBrowserPanel';
import StandardsContentContext from '../standardsContentContext';
import { buildChildrenMap, getAllExpandableIds, getAllGradeKeys, groupByGrade } from '../standardsTreeUtils';
import { StateOption } from '../types';
import useStandards from '../useStandards';
import useStandardsStates from '../useStandardsStates';

interface StandardsPageProps {
  programId: number;
  programName: string;
}

const StandardsPageInner = ({ programId, programName }: StandardsPageProps) => {
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [expandedGrades, setExpandedGrades] = useState<Set<string>>(new Set());
  const [expandedContentLeaves, setExpandedContentLeaves] = useState<Set<number>>(new Set());

  const { data: states = [] } = useStandardsStates();
  const { data: standardsData, isLoading } = useStandards({
    programId,
    stateId: selectedStateId,
  });

  const standards = standardsData?.data ?? [];

  const stateOptions: StateOption[] = states.map(s => ({
    label: `${s.name} (${s.abbreviation})`,
    value: String(s.id),
  }));

  const selectedState = states.find(s => s.id === selectedStateId);
  const stateName = selectedState?.name ?? null;
  const stateLabel = selectedState ? `${selectedState.name} (${selectedState.abbreviation})` : null;

  const handleStateChange = (stateId: number | null) => {
    setSelectedStateId(stateId);
    setSelectedGrade('');
    setExpandedNodes(new Set());
    setExpandedGrades(new Set());
    setExpandedContentLeaves(new Set());
    setIsExpanded(false);
  };

  const toggleContentLeaf = useCallback((id: number) => {
    setExpandedContentLeaves((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const contentContext = useMemo(
    () => ({ expandedContentLeaves, programId, programName, stateLabel, toggleContentLeaf }),
    [expandedContentLeaves, programId, programName, stateLabel, toggleContentLeaf],
  );

  const handleToggleNode = (id: number) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const handleToggleGrade = (grade: string) => {
    setExpandedGrades((prev) => {
      const next = new Set(prev);

      if (next.has(grade)) {
        next.delete(grade);
      } else {
        next.add(grade);
      }

      return next;
    });
  };

  const handleExpandToggle = () => {
    if (isExpanded) {
      setExpandedNodes(new Set());
      setExpandedGrades(new Set());
    } else {
      const childrenMap = buildChildrenMap(standards);
      const gradeGroups = groupByGrade(standards, childrenMap);
      setExpandedNodes(new Set(getAllExpandableIds(childrenMap)));
      setExpandedGrades(new Set(getAllGradeKeys(gradeGroups)));
    }
    setIsExpanded(prev => !prev);
  };

  return (
    <StandardsContentContext.Provider value={contentContext}>
      <StandardsBrowserPanel
        expandedGrades={expandedGrades}
        expandedNodes={expandedNodes}
        grade={selectedGrade}
        hasData={standards.length > 0}
        isExpanded={isExpanded}
        isLoading={isLoading}
        searchQuery={searchQuery}
        standards={standards}
        stateId={selectedStateId}
        stateName={stateName}
        stateOptions={stateOptions}
        onExpandToggle={handleExpandToggle}
        onGradeChange={setSelectedGrade}
        onSearchChange={setSearchQuery}
        onStateChange={handleStateChange}
        onToggleGrade={handleToggleGrade}
        onToggleNode={handleToggleNode}
      />
    </StandardsContentContext.Provider>
  );
};

const StandardsPage = ({ programId, programName }: StandardsPageProps) => (
  <ReactQueryProvider>
    <StandardsPageInner programId={programId} programName={programName} />
  </ReactQueryProvider>
);

export default StandardsPage;
